const INVENTORY_METADATA_SET_IDS = new Set(['INVENTORY-MODE', 'INVENTORY-BARCODE']);

function normalizeText(value) {
  return String(value || '').trim();
}

function normalizeTargetId(value) {
  return normalizeText(value).toUpperCase();
}

function parseQty(value) {
  const parsed = Number(String(value ?? '').trim());
  return Number.isFinite(parsed) ? parsed : 0;
}

function appendNote(existing, addition) {
  const left = normalizeText(existing);
  const right = normalizeText(addition);
  if (!right) return left;
  return left ? `${left} | ${right}` : right;
}

export function inventoryItemType(item) {
  const direct = normalizeText(item?.itemType || item?.['Item Type']);
  if (direct) return direct;
  const match = /(?:^|\|)\s*item\s*type\s*:\s*([^|]+)/i.exec(normalizeText(item?.notes));
  return normalizeText(match?.[1]);
}

export function isMedicationCatalogItem(item) {
  return inventoryItemType(item).toLowerCase() === 'medication';
}

export function expirationYear(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(normalizeText(value));
  return match ? Number(match[1]) : 0;
}

export function isActivePhysicalInventoryRecord(record, targetId = '') {
  const setId = normalizeTargetId(record?.setId);
  if (!setId || INVENTORY_METADATA_SET_IDS.has(setId)) return false;
  if (targetId && setId !== normalizeTargetId(targetId)) return false;
  if (record?.returnedToLogistics) return false;
  return parseQty(record?.requiredQty) > 0;
}

export function closeoutReason(catalogItem, record, closeoutYear) {
  const year = Number(closeoutYear) || new Date().getFullYear();
  const nextYear = year + 1;
  const expYear = expirationYear(record?.expirationDate);
  const medication = isMedicationCatalogItem(catalogItem);
  const expiresNextCalendarYear = expYear === nextYear;
  const overdue = Boolean(expYear && expYear <= year);

  if (!medication && !expiresNextCalendarYear && !overdue) return null;

  const reasons = [];
  if (medication) reasons.push('Medication');
  if (overdue) reasons.push(expYear < year ? `Expired ${expYear}` : `Expires ${year}`);
  else if (expiresNextCalendarYear) reasons.push(`Expires ${nextYear}`);
  return {
    medication,
    expiresNextCalendarYear,
    overdue,
    label: reasons.join(' · '),
  };
}

export function buildSeasonCloseoutPlan(catalog = [], expirations = [], targetId, closeoutYear = new Date().getFullYear()) {
  const catalogById = new Map((Array.isArray(catalog) ? catalog : []).map(item => [normalizeText(item?.catalogId), item]));
  const target = normalizeTargetId(targetId);
  const allActive = (Array.isArray(expirations) ? expirations : []).filter(record => isActivePhysicalInventoryRecord(record, target));
  const pull = [];

  allActive.forEach(record => {
    const catalogItem = catalogById.get(normalizeText(record?.catalogId)) || null;
    if (!catalogItem) return;
    const reason = closeoutReason(catalogItem, record, closeoutYear);
    if (!reason) return;
    pull.push({
      record,
      catalogItem,
      reason,
      catalogId: normalizeText(record?.catalogId),
      item: normalizeText(record?.item || catalogItem?.item),
      component: normalizeText(record?.component || catalogItem?.component),
      itemType: inventoryItemType(catalogItem),
      quantity: Math.max(0, parseQty(record?.requiredQty)),
      expirationDate: normalizeText(record?.expirationDate),
      lotSerial: normalizeText(record?.lotSerial),
    });
  });

  pull.sort((a, b) => {
    if (a.reason.medication !== b.reason.medication) return a.reason.medication ? -1 : 1;
    const component = a.component.localeCompare(b.component);
    if (component) return component;
    const item = a.item.localeCompare(b.item);
    if (item) return item;
    return a.expirationDate.localeCompare(b.expirationDate);
  });

  const requiredUnits = pull.reduce((sum, row) => sum + row.quantity, 0);
  const medicationUnits = pull.filter(row => row.reason.medication).reduce((sum, row) => sum + row.quantity, 0);
  const datedSupplyUnits = pull.filter(row => !row.reason.medication).reduce((sum, row) => sum + row.quantity, 0);
  const remainingAssignedUnits = allActive.reduce((sum, record) => sum + Math.max(0, parseQty(record?.requiredQty)), 0) - requiredUnits;

  return {
    targetId: target,
    closeoutYear: Number(closeoutYear) || new Date().getFullYear(),
    nextCalendarYear: (Number(closeoutYear) || new Date().getFullYear()) + 1,
    rows: pull,
    requiredUnits,
    medicationUnits,
    datedSupplyUnits,
    remainingAssignedUnits: Math.max(0, remainingAssignedUnits),
    complete: requiredUnits === 0,
  };
}

function recordMatchesTraceability(row, parsed) {
  if (!parsed) return true;
  const hasTraceability = Boolean(parsed.lot || parsed.serial || parsed.expirationDate);
  if (!hasTraceability) return true;
  const record = row?.record || {};
  const savedLot = normalizeText(record.lotSerial).toLowerCase();
  const parsedLot = normalizeText(parsed.lot).toLowerCase();
  const parsedSerial = normalizeText(parsed.serial).toLowerCase();
  const savedExpiration = normalizeText(record.expirationDate);

  if (parsedLot && savedLot && parsedLot !== savedLot) return false;
  if (!parsedLot && parsedSerial && savedLot && parsedSerial !== savedLot && !normalizeText(record.notes).toLowerCase().includes(parsedSerial)) return false;
  if (parsed.expirationDate && savedExpiration && normalizeText(parsed.expirationDate) !== savedExpiration) return false;
  return true;
}

export function findSeasonCloseoutScanCandidates(planRows = [], mappedCatalogIds = [], parsed = null) {
  const allowed = new Set((Array.isArray(mappedCatalogIds) ? mappedCatalogIds : []).map(normalizeText).filter(Boolean));
  if (!allowed.size) return [];
  const rows = (Array.isArray(planRows) ? planRows : []).filter(row => allowed.has(normalizeText(row?.catalogId)));
  const traceable = rows.filter(row => recordMatchesTraceability(row, parsed));
  return traceable.length ? traceable : rows;
}

export function findSeasonCloseoutMedicationRows(planRows = [], mappedCatalogIds = []) {
  const allowed = new Set((Array.isArray(mappedCatalogIds) ? mappedCatalogIds : []).map(normalizeText).filter(Boolean));
  if (!allowed.size) return [];
  return (Array.isArray(planRows) ? planRows : []).filter(row => row?.reason?.medication && allowed.has(normalizeText(row?.catalogId)));
}

export function mergeSeasonCloseoutManifest(currentRows = [], incomingRows = []) {
  const byRecordId = new Map();
  [...(Array.isArray(currentRows) ? currentRows : []), ...(Array.isArray(incomingRows) ? incomingRows : [])].forEach(row => {
    const recordId = normalizeText(row?.record?.recordId);
    if (recordId) byRecordId.set(recordId, row);
  });
  return [...byRecordId.values()];
}

export function summarizeSeasonCloseoutManifest(rows = []) {
  const safeRows = Array.isArray(rows) ? rows.filter(row => row?.record?.recordId) : [];
  const catalogIds = new Set(safeRows.map(row => normalizeText(row?.catalogId)).filter(Boolean));
  return {
    rows: safeRows,
    recordCount: safeRows.length,
    medicationCount: catalogIds.size,
    units: safeRows.reduce((sum, row) => sum + Math.max(0, parseQty(row?.quantity ?? row?.record?.requiredQty)), 0),
  };
}

export function makeSeasonCloseoutReturnRecord(record, closeoutYear = new Date().getFullYear(), returnedDate = '') {
  if (!record?.recordId) throw new Error('Inventory record is required for season closeout.');
  const date = normalizeText(returnedDate) || new Date().toISOString().slice(0, 10);
  return {
    ...record,
    returnedToLogistics: true,
    returnedDate: date,
    notes: appendNote(record?.notes, `Season closeout ${Number(closeoutYear) || new Date().getFullYear()}`),
    updatedAt: '',
  };
}

export function makeSeasonCloseoutReturnRecords(rows = [], closeoutYear = new Date().getFullYear(), returnedDate = '') {
  const seen = new Set();
  return (Array.isArray(rows) ? rows : []).flatMap(row => {
    const record = row?.record || row;
    const recordId = normalizeText(record?.recordId);
    if (!recordId || seen.has(recordId)) return [];
    seen.add(recordId);
    return [makeSeasonCloseoutReturnRecord(record, closeoutYear, returnedDate)];
  });
}
